import React, { createContext, useContext, useEffect, useState } from 'react';
import api from '../lib/api';

const AuthContext = createContext({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [profileLoading, setProfileLoading] = useState(false);

  // Restore session on mount
  useEffect(() => {
    const restoreSession = async () => {
      const token = localStorage.getItem('accessToken');
      if (!token) {
        setLoading(false);
        return;
      }

      try {
        const { data } = await api.get('/auth/me');
        setUser(data);
        setUserProfile(data);
      } catch {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
      } finally {
        setLoading(false);
      }
    };

    restoreSession();
  }, []);

  // Listen for forced logout from token refresh failure
  useEffect(() => {
    const handleLogout = () => {
      setUser(null);
      setUserProfile(null);
    };
    window.addEventListener('auth:logout', handleLogout);
    return () => window.removeEventListener('auth:logout', handleLogout);
  }, []);

  const signIn = async (email, password) => {
    try {
      const { data } = await api.post('/auth/login', { email, password });
      localStorage.setItem('accessToken', data.accessToken);
      localStorage.setItem('refreshToken', data.refreshToken);
      setUser(data.user);
      setUserProfile(data.user);
      return { data, error: null };
    } catch (error) {
      const requiresVerification = error.response?.data?.requiresVerification === true;
      const message =
        requiresVerification
          ? error.response?.data?.message || 'يرجى تأكيد بريدك الإلكتروني أولاً'
          : error.response?.data?.message || 'فشل تسجيل الدخول. يرجى المحاولة مرة أخرى.';
      return {
        data: null,
        error: { message, requiresVerification },
      };
    }
  };

  const signUp = async ({ fullName, email, password, title, bio, avatarUrl, selectedCircleSlugs }) => {
    try {
      const { data } = await api.post('/auth/register', {
        fullName,
        email,
        password,
        title: title || null,
        bio: bio || null,
        avatarUrl: avatarUrl || null,
        selectedCircleSlugs: selectedCircleSlugs || [],
      });
      // Registration now requires email verification - no tokens returned
      return {
        data: {
          success: data.success,
          message: data.message,
          email: data.email,
          requiresVerification: true,
        },
        error: null,
      };
    } catch (error) {
      const message = error.response?.data?.message || 'فشل إنشاء الحساب. يرجى المحاولة مرة أخرى.';
      return { data: null, error: { message } };
    }
  };

  const signOut = async () => {
    try {
      await api.post('/auth/logout');
    } catch {
      // Logout API call failed, but we still clear local state
    }
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    setUser(null);
    setUserProfile(null);
    return { error: null };
  };

  const updateProfile = async (updates) => {
    if (!user) return { data: null, error: { message: 'No user logged in' } };

    setProfileLoading(true);
    try {
      const { data } = await api.put('/users/profile', updates);
      setUserProfile(data);
      return { data, error: null };
    } catch (error) {
      const message = error.response?.data?.message || 'فشل تحديث الملف الشخصي.';
      return { data: null, error: { message } };
    } finally {
      setProfileLoading(false);
    }
  };

  const value = {
    user,
    userProfile,
    loading,
    profileLoading,
    signIn,
    signUp,
    signOut,
    updateProfile,
    isAuthenticated: !!user,
    isAdmin: user?.role === 'admin',
    isSuperAdmin: user?.isSuperAdmin === true,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
